import type { SetCopy } from '../catalog'

// Machines pushing the frontier, as bigger display builds.
export const FRONTIER: SetCopy[] = [
  {
    slug: 'starship-catch',
    series: 'frontier',
    number: 1,
    name: 'Starship Catch',
    maker: 'SpaceX',
    year: 2024,
    tagline: 'Mechazilla closes its chopsticks on Super Heavy.',
    story:
      'A rocket booster the height of a skyscraper flew home and was plucked out of the air by the tower that launched it. The 61 cm tower is dark lattice between light corner posts, grille tiles on sideways plates; the booster is round, curved slopes pinwheeled round a core of side-stud bricks, and it hangs from the chopsticks by its grid fins, high over the six-legged launch mount it flew from.',
    age: '18+',
    palette: {
      cover: '#C9CDD1',
      flood: '#8E959C',
      ink: '#15181B',
      ground: '#DDE0E3',
      accent: '#C6F432',
    },
    aliases: [
      'starship',
      'super heavy',
      'mechazilla',
      'booster catch',
      'chopsticks',
      'spacex',
      'rocket',
      'starbase',
    ],
  },
  {
    slug: 'anduril-fury',
    series: 'frontier',
    number: 2,
    name: 'Fury',
    maker: 'Anduril',
    year: 2025,
    tagline: 'A fighter with no one inside, flying wingman.',
    story:
      'Fury, the YFQ-44A, is a collaborative combat aircraft: a jet with no one inside, built to fly alongside crewed fighters and take on the risky jobs. Its trapezoidal wings and stabilators are plate sheets under a tiled skin, each leading edge one straight 45-degree line of cut tiles meeting corner to corner. The nose narrows along wing plates cut at a single angle, course over course, and closes in a curved wedge cone above the chin intake; the single fin is two tiled sheets stood back to back on the spine.',
    age: '16+',
    palette: {
      cover: '#A7AEB5',
      flood: '#6E767E',
      ink: '#101418',
      ground: '#D8DCE0',
      accent: '#C6F432',
    },
    aliases: [
      'fury',
      'yfq-44a',
      'fq-44',
      'cca',
      'collaborative combat aircraft',
      'loyal wingman',
      'drone jet',
      'anduril',
    ],
  },
  {
    slug: 'anduril-roadrunner',
    series: 'frontier',
    number: 3,
    name: 'Roadrunner-M',
    maker: 'Anduril',
    year: 2023,
    tagline: 'Takes off on its tail, and lands on it too.',
    story:
      'Roadrunner is a 1.8 m twin-jet interceptor that launches straight up, chases down a threat and, if it isn’t needed, comes back to land upright. The delta is built the way MOC wings are: a web of side-stud bricks with skins clipped on front and back, wing plates falling one stud in two for a single straight leading edge, and a round forebody with a silver seeker dome.',
    age: '16+',
    palette: {
      cover: '#2A2E33',
      flood: '#16191C',
      ink: '#E9F7B8',
      ground: '#D6D9DC',
      accent: '#C6F432',
    },
    aliases: [
      'roadrunner',
      'roadrunner-m',
      'interceptor',
      'vtol',
      'counter drone',
      'anduril',
    ],
  },
  {
    slug: 'anduril-dive-xl',
    series: 'frontier',
    number: 4,
    name: 'Dive-XL',
    maker: 'Anduril',
    year: 2024,
    tagline: 'A bus-sized submarine that sails itself.',
    story:
      'Dive-XL is an extra-large autonomous undersea vehicle, built to leave the pier alone and spend weeks at depth. Curved slopes round its hull over the top and inverted slopes carry it underneath; the flat flanks are tiles on side studs, with the payload bay door outlined in black. Dishes dome the nose and the tail cone, an X of four fins is built flat and turned about the hull, and it rests on a yellow two-saddle cradle.',
    age: '16+',
    palette: {
      cover: '#5B6168',
      flood: '#383D42',
      ink: '#F2F3F4',
      ground: '#D4D8DB',
      accent: '#F2C12E',
    },
    aliases: [
      'dive-xl',
      'dive xl',
      'xl-auv',
      'ghost shark',
      'submarine',
      'autonomous submarine',
      'uuv',
      'anduril',
    ],
  },
  {
    slug: 'asml-euv',
    series: 'frontier',
    number: 5,
    name: 'EUV Lithography Machine',
    maker: 'ASML',
    year: 2017,
    tagline: 'The machine that prints the world’s chips.',
    story:
      'Inside a white cabinet the size of a bus, laser pulses turn tin droplets into extreme ultraviolet light that prints chip patterns finer than a virus. The front is one big sideways tile mosaic, the source vessel is a stack of round bricks, and it all stands on a gridded cleanroom floor.',
    age: '16+',
    palette: {
      cover: '#F1F3F5',
      flood: '#DCE3EA',
      ink: '#0B2E6B',
      ground: '#EEF1F4',
      accent: '#1E5AA8',
    },
    aliases: [
      'asml',
      'euv',
      'lithography',
      'twinscan',
      'nxe',
      'exe',
      'chip machine',
      'semiconductor',
    ],
  },
  {
    slug: 'sparc-tokamak',
    series: 'frontier',
    number: 6,
    name: 'SPARC Tokamak',
    maker: 'Commonwealth Fusion Systems',
    year: 2025,
    tagline: 'A small star in a very strong magnetic bottle.',
    story:
      'SPARC is a compact tokamak built to show that fusion can give out more energy than it takes in, thanks to a new generation of high-temperature superconducting magnets. Eighteen D-shaped toroidal field coils ring its vacuum vessel round the copper central solenoid, each built upright one stud wide, its edge curving over the top on curved and straight slopes that each sit a stud further out and fall steeper than the last. A quarter of the machine is cut away, four coils with it, to show the section: the vessel wall, the vacuum, and the plasma glowing pink at its core. A technician stands by for scale.',
    age: '16+',
    palette: {
      cover: '#9AA1A8',
      flood: '#4A5057',
      ink: '#FFF4E0',
      ground: '#DADDE0',
      accent: '#F2A33A',
    },
    aliases: [
      'sparc',
      'tokamak',
      'fusion',
      'fusion reactor',
      'cfs',
      'commonwealth fusion',
    ],
  },
  {
    slug: 'boom-xb1',
    series: 'frontier',
    number: 7,
    name: 'XB-1',
    maker: 'Boom Supersonic',
    year: 2024,
    tagline: 'Baby Boom, the jet that broke the sound barrier on its own.',
    story:
      'XB-1 was a one-third-scale demonstrator for a supersonic airliner, and in 2025 it became the first independently built jet to go faster than sound. Its delta wing sweeps back in wedge plates laid lengthwise, one row at a time, under a tiled skin.',
    age: '16+',
    palette: {
      cover: '#F4F4F4',
      flood: '#DDE2E8',
      ink: '#10233F',
      ground: '#EEF0F3',
      accent: '#1E5AA8',
    },
    aliases: [
      'xb-1',
      'xb1',
      'baby boom',
      'boom',
      'supersonic',
      'overture',
      'concorde',
    ],
  },
  {
    slug: 'humanoid-robot',
    series: 'frontier',
    number: 8,
    name: 'Humanoid Robot',
    maker: 'Tesla',
    year: 2023,
    tagline: 'A general-purpose robot, built to our shape.',
    story:
      'Humanoid robots are meant to walk into spaces made for people and take on the dull, repetitive work. This one follows the Gen 3 renders found in the Tesla app, in Tan for the champagne: a chest shield whose sides curve back, the black belly rising into it in a broad U, shoulders rounding off on curved slopes, gold arms and thighs, black from the knee down, and an egg-shaped Tan helmet with a glossy black visor wrapped over the chin and cheeks. Every limb is a round column of side-stud bricks skinned on all four faces; the arms are built on their own and hang from hinge bricks, swung a little out from the body, with a grille brick for the fingers and a round-brick thumb, and the figure stands on a slim black plinth.',
    age: '16+',
    palette: {
      cover: '#E9EAEB',
      flood: '#1C1F22',
      ink: '#F5F5F5',
      ground: '#DEE0E2',
      accent: '#C6F432',
    },
    aliases: [
      'humanoid',
      'robot',
      'optimus',
      'tesla bot',
      'android',
      'figure',
      'general purpose robot',
    ],
  },
]
